<script setup lang="ts">
import type { Csv } from '@/apis/generated/dialbunny';
import { CsvFieldMapperUtil } from '@/csv/utils/csv-field-mapper-util';
import type {
  CsvCustomFieldMap,
  CsvDPFieldMap,
} from '@/csv/utils/csv-field-mapper-util';
import {
  getStoredCsvCustomMap,
  getStoredCsvMap,
  storeCsvCustomMap,
  storeCsvMap,
} from '@/csv/utils/csv-map-storage';
import { nextTick, onMounted, provide, ref, watch } from 'vue';
import type { Ref } from 'vue';
import CsvFieldMapper from '../CsvFieldMapper/CsvFieldMapper.vue';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import { useCsvStore } from '@/stores';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtToast } from '@dialpad/dialtone/vue3/lib/toast';
import { DtStack } from '@dialpad/dialtone/vue3/lib/stack';
import { useI18N } from '@dialpad/i18n';
import { DATA_QA } from './csv-import-map-step.constants';
import { handlePatchErrorFactory } from '@/csv/utils/csv-handle-patch-error';
import logger from '@/logger';

const log = logger.getChild('CsvImportMapStep');

log.debug('Mounting');

onMounted(() => {
  log.debug('Mounted');
});

const { $ta } = useI18N();

const usesStoredMap = ref(false);
const errors = ref(new Map<string, string | null>());
const loading = ref(false);

const csvStore = useCsvStore();

const validationTrigger = ref(0);
provide(TRIGGER_FORM_VALIDATION, validationTrigger);

const setError = (key: string, error: string | null) => {
  log.debug('Setting error', key, error);
  errors.value.set(key, error);
};

const csvFieldMapperUtil = new CsvFieldMapperUtil();

const csv = defineModel<Csv | null>({
  required: true,
});

const props = defineProps<{
  examples: string[];
}>();

const emit = defineEmits(['continue', 'back']);

watch(
  csv,
  // eslint-disable-next-line complexity
  async () => {
    log.debug('csv changed', csv.value);
    if (!csv.value) return;
    const hasMapFromBackend =
      Object.keys(csv.value.leadMapping ?? {}).length > 0;
    const hasCustomMapFromBackend =
      Object.keys(csv.value.customMapping ?? {}).length > 0;

    const storedMap = hasMapFromBackend
      ? csv.value.leadMapping ?? {}
      : await getStoredCsvMap(
          csv.value.companyId.toString(),
          csv.value.columns ?? [],
        );

    const storedCustomMap = hasCustomMapFromBackend
      ? csv.value.customMapping ?? {}
      : await getStoredCsvCustomMap(
          csv.value.companyId.toString(),
          csv.value.columns ?? [],
        );

    log.debug(`hasMapFromBackend: ${hasMapFromBackend}, stored map`, storedMap);

    usesStoredMap.value = !!storedMap || !!storedCustomMap;

    log.debug(
      'Initializing csvFieldMapperUtil',
      csv.value.columns,
      'examples:',
      props.examples,
    );
    csvFieldMapperUtil.init(
      {
        map: (storedMap ?? {}) as CsvDPFieldMap,
        customMap: (storedCustomMap ?? {}) as CsvCustomFieldMap,
      },
      csv.value.columns ?? [],
      props.examples,
    );
  },
  { immediate: true },
);

const validate = async (): Promise<boolean> => {
  log.debug('Validating..');
  validationTrigger.value++;

  await nextTick();

  const isValid = [...errors.value.values()].every((error) => error === null);

  log.debug('Validation result:', isValid);
  return isValid;
};

const handlePatchError = handlePatchErrorFactory($ta('CSV_IMPORT_PATCH_ERROR'));

// eslint-disable-next-line max-statements
const submit = async () => {
  log.debug('Submitting..');
  if (!(await validate())) {
    return;
  }
  const id = csv.value?.id;
  if (!id) {
    throw new Error('Csv id is not set');
  }

  if (!csvFieldMapperUtil.validate()) {
    log.debug('csvFieldMapperUtil validation failed');
    return;
  }
  const mapping = csvFieldMapperUtil.map as Ref<Record<string, string>>;
  const customMapping = csvFieldMapperUtil.customMap as Ref<
    Record<string, string>
  >;
  loading.value = true;
  log.debug('Updating csv record...');
  csvStore.update(
    id,
    {
      leadMapping: mapping.value,
      customMapping: customMapping.value,
    },
    {
      onSuccess: async (updated) => {
        csv.value = updated;
        log.debug('Update successful');
        if (csv.value.columns) {
          log.debug('Storing csv map');
          await storeCsvMap(
            csv.value.companyId.toString(),
            csv.value.columns,
            mapping.value,
          );
          await storeCsvCustomMap(
            csv.value.companyId.toString(),
            csv.value.columns,
            customMapping.value,
          );
          log.debug('Csv map stored');
        }
        emit('continue');
        loading.value = false;
      },
      onRetry: () => {
        // show toast
      },
      onError: async (error) => {
        // show toast
        await handlePatchError(error);
        loading.value = false;
      },
    },
  );
};
</script>
<template>
  <div :data-qa="DATA_QA.ROOT" class="csv-import-map-step">
    <p class="d-fc-tertiary">
      {{ $t('CSV_IMPORT_MAP_SUBTITLE') }}
    </p>
    <div class="d-ba d-baw1 d-bc-default d-bar8 d-my16">
      <DtToast
        :message="$t('CSV_IMPORT_FAMILIAR_FORMAT')"
        :show="usesStoredMap"
        kind="success"
        :hide-icon="true"
        :close-button-props="{
          'data-qa': DATA_QA.FAMILIAR_NOTICE_CLOSE,
          ariaLabel: $t('CSV_IMPORT_CLOSE_FAMILIAR_TOAST_ARIA_LABEL'),
        }"
        @close="usesStoredMap = false"
      />
      <CsvFieldMapper
        :csv-field-mapper-util="csvFieldMapperUtil"
        :fields="csv?.columns ?? []"
        @update:error="(key, value) => setError(key, value)"
      />
    </div>
    <div class="d-d-flex d-jc-flex-end d-mt16">
      <DtStack direction="row" gap="400">
        <DtButton
          :data-qa="DATA_QA.BACK"
          importance="clear"
          @click="emit('back')"
        >
          {{ $t('CSV_IMPORT_BACK') }}
        </DtButton>
        <DtButton :data-qa="DATA_QA.NEXT" :loading="loading" @click="submit">
          {{ $t('CSV_IMPORT_CONTINUE') }}
        </DtButton>
      </DtStack>
    </div>
  </div>
</template>
<style>
.csv-import-map-step .d-toast {
  max-width: 100% !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  box-shadow: none !important;
  border-bottom: 1px solid var(--dt-color-border-default) !important;
  background-clip: padding-box !important;
  padding-top: 16px !important;
  padding-bottom: 16px !important;
}
</style>
